// Arc gauge for "progress toward a threshold" content — skill-progress percentage,
// package hours used/remaining, lesson-completion. Reuses the logo's own dial
// geometry (a 270° sweep, teal fill, gray track) so progress has a visual language
// specific to this product instead of a generic flat progress bar.
//
// Deliberately NOT used for list/table rows or anything that isn't itself a
// progress-toward-a-threshold value — see the design plan for scope.

const SWEEP_DEGREES = 270
const START_ANGLE = -225 // sweep starts at bottom-left, matching the logo mark

function polarToCartesian(cx: number, cy: number, r: number, angleDeg: number) {
  const a = (angleDeg * Math.PI) / 180
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) }
}

function arcPath(cx: number, cy: number, r: number, startDeg: number, endDeg: number) {
  const start = polarToCartesian(cx, cy, r, startDeg)
  const end = polarToCartesian(cx, cy, r, endDeg)
  const largeArc = endDeg - startDeg <= 180 ? 0 : 1
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`
}

export function GaugeProgress({
  value, max, size = 72, label, color = '#0F7C6C',
}: {
  value: number
  max: number
  size?: number
  label?: string
  color?: string
}) {
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0
  const cx = size / 2
  const cy = size / 2
  const r = size / 2 - 6
  const endAngle = START_ANGLE + SWEEP_DEGREES * pct

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role='img' aria-label={label ?? `${Math.round(pct * 100)}% complete`}>
        <path
          d={arcPath(cx, cy, r, START_ANGLE, START_ANGLE + SWEEP_DEGREES)}
          fill='none' stroke='rgba(27,42,74,0.1)' strokeWidth={6} strokeLinecap='round'
        />
        {pct > 0 && (
          <path
            d={arcPath(cx, cy, r, START_ANGLE, endAngle)}
            fill='none' stroke={color} strokeWidth={6} strokeLinecap='round'
          />
        )}
        <text
          x={cx} y={cy + 4} textAnchor='middle'
          fontFamily='Lora, serif' fontWeight={600} fontSize={size * 0.22} fill='var(--ink)'
        >
          {Math.round(pct * 100)}%
        </text>
      </svg>
      {label && <div style={{ font: 'var(--text-meta)', color: 'var(--ink-60)', textAlign: 'center' }}>{label}</div>}
    </div>
  )
}
